import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FileArrowDown, CaretDown } from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "pendente", label: "Pendente (PTAX)" },
  { value: "pendente_vinculacao", label: "Pendente Vinculação" },
  { value: "vinculada", label: "Vinculada" },
  { value: "cancelada", label: "Cancelada" },
  { value: "solicitacao_cancelamento", label: "Cancelamento Solicitado" },
];

const TIPO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "trava", label: "Trava" },
  { value: "ptax", label: "PTAX" },
  { value: "ddf", label: "DDF" },
];

const DEFAULT_STATUS = STATUS_OPTIONS.map(o => o.value);

const TIPO_BADGE = {
  trava: { label: "TRAVA", bg: "#0D2A4A", color: "#60A5FA" },
  ptax: { label: "PTAX", bg: "#3D2B00", color: "#FBBF24" },
  ddf: { label: "DDF", bg: "#2D1B4E", color: "#A78BFA" },
};

export default function Relatorio() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    cliente: "",
    vendedor_id: "",
    status: [...DEFAULT_STATUS],
    tipo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef(null);

  useEffect(() => {
    api.get("/usuarios/vendedores").then(r => setVendedores(r.data)).catch(() => {});
    loadWithFilters({ from_date: "", to_date: "", cliente: "", vendedor_id: "", status: [...DEFAULT_STATUS], tipo: "" });
    const handler = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const buildParams = (f) => {
    const params = new URLSearchParams();
    if (f.from_date) params.append("from_date", f.from_date);
    if (f.to_date) params.append("to_date", f.to_date);
    if (f.cliente) params.append("cliente", f.cliente);
    if (f.vendedor_id) params.append("vendedor_id", f.vendedor_id);
    if (f.tipo) params.append("tipo", f.tipo);
    f.status.forEach(s => params.append("status", s));
    return params;
  };

  const loadWithFilters = async (f) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/relatorio/margem?${buildParams(f).toString()}`);
      setData(res);
      setAppliedFilters(f);
    } catch {
      toast.error("Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  };

  const load = () => loadWithFilters(filters);

  const fmt = (n, dec = 2) =>
    n?.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

  const fmtDataTrava = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const fmtDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const renderVencimento = (t) => {
    if (t.tipo_display === "ddf") return `DDF · ${t.ddf_dias ?? "?"} dias`;
    if (t.tipo_display === "ptax" && !t.data_vencimento) return "PTAX";
    return fmtDate(t.data_vencimento) || "—";
  };

  const toggleStatus = (val) => {
    setFilters(f => ({
      ...f,
      status: f.status.includes(val) ? f.status.filter(s => s !== val) : [...f.status, val],
    }));
  };

  const statusLabel =
    filters.status.length === STATUS_OPTIONS.length
      ? "Todos"
      : filters.status.length === 0
      ? "Nenhum"
      : `${filters.status.length} selecionados`;

  const exportCsv = async () => {
    if (!appliedFilters || exporting) return;
    setExporting(true);
    try {
      await exportCsvFull();
    } catch {
      toast.error("Erro ao exportar CSV");
    } finally {
      setExporting(false);
    }
  };

  const exportCsvFull = async () => {
    // A tabela da tela vem truncada pelo LIMIT do backend; o CSV precisa do
    // conjunto inteiro, então refaz a consulta com limit=0 (sem limite).
    const params = buildParams(appliedFilters);
    params.append("limit", "0");
    const { data: full } = await api.get(`/relatorio/margem?${params.toString()}`);
    const travas = full?.travas || [];
    if (!travas.length) {
      toast.error("Nenhum registro para exportar");
      return;
    }
    const fmtBR = (n, dec = 2) =>
      n != null ? Number(n).toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec }) : "";
    const header = ["Código", "Pedido", "Vendedor", "Cliente", "MONTANTE USD", "Data da Trava", "Taxa", "Taxa SYSFÉRTIL", "Spot", "Spread", "Margem BRL", "VALOR EM R$", "Vencimento", "Prazo", "Tipo", "Status"];
    const rows = travas.map(t => {
      const montante = t.volume_usd != null ? Number(t.volume_usd) : null;
      const taxa = t.taxa_final != null ? Number(t.taxa_final) : null;
      const valorBRL = montante != null && taxa != null ? montante * taxa : null;
      return [
        t.codigo,
        t.numero_pedido || "",
        t.vendedor_nome,
        t.cliente,
        fmtBR(montante, 2),
        fmtDataTrava(t.data_trava),
        fmtBR(t.taxa_final, 4),
        fmtBR(t.taxa_csv, 4),
        fmtBR(t.spot != null ? Math.round(t.spot * 1000) / 1000 : null, 3),
        t.spread_realizado != null ? fmtBR(t.spread_realizado, 4) : "",
        fmtBR(t.margem_liquida, 2),
        fmtBR(valorBRL, 2),
        renderVencimento(t),
        t.prazo_dias,
        (t.tipo_display || "").toUpperCase(),
        t.status,
      ];
    });
    const totalMontante = travas.reduce((s, t) => s + (t.volume_usd != null ? Number(t.volume_usd) : 0), 0);
    const totalValorBRL = travas.reduce((s, t) => {
      const m = t.volume_usd != null ? Number(t.volume_usd) : 0;
      const tx = t.taxa_final != null ? Number(t.taxa_final) : 0;
      return s + m * tx;
    }, 0);
    const totalRow = ["TOTAL", "", "", "", fmtBR(totalMontante, 2), "", "", "", "", "", "", fmtBR(totalValorBRL, 2), "", "", "", ""];
    const csv = [header, ...rows, totalRow].map(r => r.join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio-travas-${Date.now()}.csv`;
    a.click();
  };

  const selectCls = "w-full mt-2 h-9 bg-[#0B2D4D] border border-[#123a5e] text-sm px-3 text-white rounded-sm appearance-none cursor-pointer";

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Relatório</p>
            <h1 className="text-3xl font-bold tracking-tight">Relatório de Travas</h1>
          </div>
          <Button onClick={exportCsv} disabled={exporting || !appliedFilters} variant="outline" data-testid="btn-exportar" className="border-[#123a5e]">
            <FileArrowDown className="w-4 h-4 mr-2" /> {exporting ? "Exportando..." : "Exportar CSV"}
          </Button>
        </div>

        {/* Filtros */}
        <div className="border border-[#123a5e] rounded-sm p-4 bg-[#0B2D4D] grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">De</Label>
            <Input
              type="date"
              data-testid="filter-from"
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
              className="bg-transparent border-[#123a5e] font-mono-data mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Até</Label>
            <Input
              type="date"
              data-testid="filter-to"
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
              className="bg-transparent border-[#123a5e] font-mono-data mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Cliente</Label>
            <Input
              data-testid="filter-cliente"
              value={filters.cliente}
              onChange={(e) => setFilters({ ...filters, cliente: e.target.value })}
              className="bg-transparent border-[#123a5e] mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Vendedor</Label>
            <select
              data-testid="filter-vendedor"
              value={filters.vendedor_id}
              onChange={(e) => setFilters({ ...filters, vendedor_id: e.target.value })}
              className={selectCls}
            >
              <option value="">Todos</option>
              {vendedores.map(v => (
                <option key={v.id} value={v.id}>{v.nome}</option>
              ))}
            </select>
          </div>

          {/* Status multi-select */}
          <div ref={statusRef} className="relative">
            <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Status</Label>
            <button
              type="button"
              data-testid="filter-status"
              onClick={() => setStatusOpen(o => !o)}
              className="w-full mt-2 h-9 bg-[#0B2D4D] border border-[#123a5e] text-sm px-3 text-left text-white rounded-sm flex items-center justify-between"
            >
              <span className="truncate">{statusLabel}</span>
              <CaretDown className={`w-3 h-3 text-[#6b89a8] shrink-0 transition-transform ${statusOpen ? "rotate-180" : ""}`} />
            </button>
            {statusOpen && (
              <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-[#0D1F33] border border-[#123a5e] rounded-sm shadow-xl">
                {STATUS_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-[#123a5e] cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={filters.status.includes(opt.value)}
                      onChange={() => toggleStatus(opt.value)}
                      className="accent-[#40B6A2]"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Tipo</Label>
            <select
              data-testid="filter-tipo"
              value={filters.tipo}
              onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
              className={selectCls}
            >
              {TIPO_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end md:col-span-2">
            <Button
              onClick={load}
              data-testid="btn-aplicar-filtro"
              className="bg-[#40B6A2] text-black hover:bg-[#359a89] w-full"
            >
              Aplicar filtros
            </Button>
          </div>
        </div>

        {/* KPIs */}
        {data?.totalizadores && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Metric label="Volume Total USD" value={`USD ${fmt(data.totalizadores.volume_total_usd)}`} testid="tot-volume" />
            <Metric label="Margem Total BRL" value={`R$ ${fmt(data.totalizadores.margem_total_brl)}`} accent="#40B6A2" testid="tot-margem" />
            <Metric label="Spread Médio" value={fmt(data.totalizadores.spread_medio, 4)} testid="tot-spread" />
            <Metric label="Quantidade" value={String(data.totalizadores.quantidade)} testid="tot-qtd" />
            <Metric label="Prazo Médio" value={`${data.totalizadores.prazo_medio ?? "—"} dias`} testid="tot-prazo-medio" />
          </div>
        )}

        {/* Tabela */}
        <div className="border border-[#123a5e] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0B2D4D] border-b border-[#123a5e]">
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#6b89a8]">
                  <th className="p-3 whitespace-nowrap">Código</th>
                  <th className="p-3 whitespace-nowrap">Pedido</th>
                  <th className="p-3 whitespace-nowrap">Vendedor</th>
                  <th className="p-3 whitespace-nowrap">Cliente</th>
                  <th className="p-3 text-right whitespace-nowrap">Volume USD</th>
                  <th className="p-3 whitespace-nowrap">Data da Trava</th>
                  <th className="p-3 text-right whitespace-nowrap">Taxa</th>
                  <th className="p-3 text-right whitespace-nowrap">Spot</th>
                  <th className="p-3 text-right whitespace-nowrap">Spread</th>
                  <th className="p-3 text-right whitespace-nowrap">Margem BRL</th>
                  <th className="p-3 whitespace-nowrap">Vencimento</th>
                  <th className="p-3 text-right whitespace-nowrap">Prazo</th>
                  <th className="p-3 whitespace-nowrap">Tipo</th>
                  <th className="p-3 whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={14} className="p-6 text-center text-[#6b89a8]">Carregando...</td></tr>
                ) : data?.travas?.length === 0 ? (
                  <tr><td colSpan={14} className="p-6 text-center text-[#6b89a8]">Nenhum dado no período</td></tr>
                ) : (
                  data?.travas?.map(t => {
                    const badge = TIPO_BADGE[t.tipo_display] || TIPO_BADGE.trava;
                    return (
                      <tr key={t.codigo} className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/50">
                        <td className="p-3 font-mono-data font-semibold text-[#40B6A2]">{t.codigo}</td>
                        <td className="p-3 font-mono-data text-xs">
                          {t.numero_pedido
                            ? <span className="text-[#40B6A2]">{t.numero_pedido}</span>
                            : <span className="text-[#6b89a8]">—</span>}
                        </td>
                        <td className="p-3 truncate max-w-[140px] text-xs">{t.vendedor_nome}</td>
                        <td className="p-3 truncate max-w-[180px]">{t.cliente}</td>
                        <td className="p-3 text-right font-mono-data">{fmt(t.volume_usd)}</td>
                        <td className="p-3 font-mono-data text-xs whitespace-nowrap" style={{ color: "#8aa9c9" }}>{fmtDataTrava(t.data_trava)}</td>
                        <td className="p-3 text-right font-mono-data">R$ {fmt(t.taxa_final, 4)}</td>
                        <td className="p-3 text-right font-mono-data text-[#8aa9c9]">R$ {fmt(t.spot, 4)}</td>
                        <td className="p-3 text-right font-mono-data">R$ {fmt(t.spread_realizado, 4)}</td>
                        <td className="p-3 text-right font-mono-data text-[#40B6A2]">{fmt(t.margem_liquida)}</td>
                        <td className="p-3 font-mono-data text-xs whitespace-nowrap">{renderVencimento(t)}</td>
                        <td className="p-3 text-right font-mono-data text-xs text-[#8aa9c9]">{t.prazo_dias}d</td>
                        <td className="p-3">
                          <span
                            className="text-[10px] font-bold tracking-widest px-2 py-1 rounded-sm"
                            style={{ background: badge.bg, color: badge.color }}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="p-3 text-xs uppercase tracking-wider text-[#8aa9c9] whitespace-nowrap">
                          {t.status?.replace(/_/g, " ")}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {data?.total_registros > (data?.travas?.length ?? 0) && (
            <div
              className="px-3 py-2 border-t border-[#123a5e] bg-[#0B2D4D] text-xs text-[#8aa9c9]"
              data-testid="aviso-truncado"
            >
              Mostrando {fmt(data.travas.length, 0)} de {fmt(data.total_registros, 0)} registros.
              Os totalizadores acima e o CSV consideram o conjunto filtrado completo.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Metric({ label, value, accent = "#FFFFFF", testid }) {
  return (
    <div className="border border-[#123a5e] rounded-sm p-5 bg-[#0B2D4D]" data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#6b89a8] mb-3">{label}</p>
      <p className="text-2xl font-bold font-mono-data" style={{ color: accent }}>{value}</p>
    </div>
  );
}
